import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';

import { SubscriptionCheckService } from 'src/app/core/subscription-check.service';
import { NativeBridgeService } from '../native-bridge/native-bridge.service';
import { PaymentAppService } from '../payment/payment-app.service';
import { SessionService } from '../session.service';
import { PublicService } from '../public/public.service';
import { StripeService } from '../payment/stripe.service';
import { RedirectService } from '../redirect/redirect.service';

// Rutas del área de cliente accesibles con el token temporal del registro (`session: false`),
// mientras el usuario completa (o omite) el flujo de compra tras registrarse. El resto de
// `/customer/**` sigue exigiendo una sesión plena (`isActiveToken()`).
const TEMP_TOKEN_ALLOWED_PREFIXES = ['/customer/subscriptions', '/customer/summary', '/customer/wallet'];

// Criterio único de "el token temporal sigue vivo en esta URL": lo comparten la guarda y la
// limpieza del layout al recargar (`ViewsComponent`), para que una F5 en la tienda no destruya
// lo que la guarda acaba de admitir.
export const isTemporaryTokenAllowedUrl = (url: string, domain: string): boolean => {
    const path = stripDomainPrefix(url, domain);

    return TEMP_TOKEN_ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix));
};

export const isLogged = (state: RouterStateSnapshot, sessionService = inject(SessionService)) => {
    if (sessionService.isActiveToken()) {
        return true;
    }

    if (sessionService.getIsTokenNoSession()) {
        return isTemporaryTokenAllowedUrl(state.url, sessionService.getDomain());
    }

    return false;
};
export const checkCenterInfo = (route: ActivatedRouteSnapshot, publicService = inject(PublicService)) => publicService.checkIfCanActivate(route);
export const checkPayment = (paymentAppService = inject(PaymentAppService)) => paymentAppService.checkPayment();
export const checkStripe = (stripeService = inject(StripeService)) => stripeService.checkStripe();
export const checkPaymentMethods = (sessionService = inject(SessionService), stripeService = inject(StripeService)) =>
    sessionService.getCenterInfo().paymentConfiguration.canManagePaymentMethods() ? true : stripeService.checkStripe();
export const checkAndRedirect = (state: RouterStateSnapshot, redirectService = inject(RedirectService)) => redirectService.checkAndRedirect(state);
export const checkSubscription = (subscriptionCheckService = inject(SubscriptionCheckService)) => subscriptionCheckService.checkSubscription();

export const canNavigateDuringReactivation = (
    state: RouterStateSnapshot,
    sessionService = inject(SessionService),
    router = inject(Router)
) => {
    if (!sessionService.isRestrictedNavigation()) {
        return true;
    }

    const allowedPrefixes = ['/customer/subscriptions', '/customer/summary'];
    // En multi-tenant la URL llega con el slug del centro prefijado (`/<slug>/customer/...`).
    // Lo quitamos antes de comparar para no fallar el `startsWith` y evitar el bucle de
    // redirección con `checkAndRedirect`, que vuelve a prefijar el slug indefinidamente.
    const path = stripDomainPrefix(state.url, sessionService.getDomain());

    if (allowedPrefixes.some(prefix => path.startsWith(prefix))) {
        return true;
    }

    return router.parseUrl('/customer/subscriptions/shop');
};

const stripDomainPrefix = (url: string, domain: string): string => {
    if (domain && (url === `/${domain}` || url.startsWith(`/${domain}/`))) {
        return url.slice(domain.length + 1) || '/';
    }

    return url;
};

export const webViewCloseGuard = (
    nativeBridge = inject(NativeBridgeService),
    router = inject(Router)
): boolean => {
    if (!nativeBridge.isNativeContext()) {
        return true;
    }
    const isPopState = router.getCurrentNavigation()?.trigger === 'popstate';
    if (!isPopState) {
        return true;
    }
    nativeBridge.closeWebView();
    return false;
};
